import React from 'react';
import { loadRemote } from '@module-federation/runtime';
import { MfWidgetRegistryContext, type WidgetRegistry } from '@brandclub/common-ui';

let validWidgetIds: Set<string> | null = null;
try {
  loadRemote<{ WIDGET_IDS: readonly string[] }>('concierge_widgets/Manifest')
    .then((mod) => {
      if (mod?.WIDGET_IDS) validWidgetIds = new Set(mod.WIDGET_IDS);
    })
    .catch(() => {});
} catch {}

function kebabToPascal(kebab: string): string {
  return kebab.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

function lazyWidget(pascalName: string) {
  return React.lazy(() =>
    loadRemote<{ default: React.ComponentType<any> }>(`concierge_widgets/${pascalName}`).then((mod) => {
      if (!mod) throw new Error(`Failed to load concierge_widgets/${pascalName}`);
      return mod;
    }),
  );
}

const widgetCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

const registry: WidgetRegistry = new Proxy({} as WidgetRegistry, {
  get(_target, prop: string) {
    if (typeof prop !== 'string') return undefined;
    if (validWidgetIds && !validWidgetIds.has(prop)) {
      console.warn(`[MfWidgetRegistry] Unknown widget ID "${prop}". Valid IDs:`, [...validWidgetIds]);
    }
    let cached = widgetCache.get(prop);
    if (!cached) {
      cached = lazyWidget(kebabToPascal(prop));
      widgetCache.set(prop, cached);
    }
    return cached;
  },
  has(_target, prop: string) {
    if (validWidgetIds) return validWidgetIds.has(prop);
    return true;
  },
});

export function MfWidgetRegistryProvider({ children }: { children: React.ReactNode }) {
  return (
    <MfWidgetRegistryContext.Provider value={registry}>
      {children}
    </MfWidgetRegistryContext.Provider>
  );
}
